import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import QuotationTenderingContext from "../QuotationTenderingContext";
import Layout from "../Layout";
import { Button } from "../../../../components";
import useQuotation from "../../../../hooks/useQuotation";
import { ObjectId } from "bson";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import DocumentUploader from "../../../../components/common/DocumentUploader";
import useTendering from "../../../../hooks/useTendering";
import AppContext from "../../../../AppContext";
import TenderingDetailContext from "../../context";
import useUsers from "../../../../hooks/useUsers";
import { format } from "date-fns";
import {
  formatNumber,
  numberToOrdinal,
} from "../../../../helpers/common.helper";

const assetsUri = import.meta.env.ASSETS_URI;

const Summary = (props) => {
  const navigate = useNavigate();
  const { user } = useContext(AppContext);
  const { details: tenderingDetail } = useContext(TenderingDetailContext);
  const { setStep, details, goBack } = useContext(QuotationTenderingContext);
  const { createQuotation, getPDF, getQuotationCode, patchQuotation } =
    useQuotation();
  const { getSignedUrl } = useTendering();
  const { getUser } = useUsers();
  const [logoFile, setLogoFile] = useState(undefined);
  const [logoPreview, setLogoPreview] = useState(undefined);
  const [owner, setOwner] = useState(undefined);
  const [sanitizedData, setSanitizedData] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setStep(3);

    if (!details) return;

    getUser(tenderingDetail.createdBy).then((data) => {
      setOwner({
        name: `${data.firstname} ${data.surname ?? ""}`,
        address: `${data.contact.address.streetAddress} ${data.contact.address.apt} ${data.contact.address.subDistrict} ${data.contact.address.district} ${data.contact.address.province} ${data.contact.address.postalCode}`,
      });
    });

    getQuotationCode().then((data) => {
      const sanitizedData = _getSanitizedData();
      setSanitizedData({ ...sanitizedData, code: data });
    });
  }, []);

  const _getSanitizedData = () => {
    const {
      _id,
      items,
      payment: { advancedPayment, paymentTerms, wht },
    } = details;

    let expectedGrandTotal = 0;

    const subjectId = new ObjectId();

    const _items = items.map((item) => {
      expectedGrandTotal += +item.price * +item.areaSize;

      return {
        _id: new ObjectId(),
        name: item.name,
        price: item.price,
        remark: item.remark,
        size: {
          value: item.areaSize,
          unit: item.areaUnit,
        },
      };
    });

    const _advancedPayment = {
      remark: advancedPayment.remark,
      days: advancedPayment.days,
      percentage: advancedPayment.percentage,
      total: roundNumber(
        expectedGrandTotal * (+advancedPayment.percentage / 100)
      ),
    };

    const _paymentTerms = paymentTerms.map((term, termIndex) => {
      let _subTotal = 0;
      const isFinalTerm = paymentTerms.length === termIndex + 1;

      const _subjectItems = term.map((item) => {
        const _total = roundNumber(
          expectedGrandTotal * (+item.percentage / 100)
        );

        _subTotal += _total;

        return {
          item: _items[Number(item.item)]._id,
          remark: item.remark,
          days: !isFinalTerm ? item.days ?? undefined : undefined,
          percentage: item.percentage,
          total: _total,
        };
      });

      return {
        subjects: [
          {
            subject: subjectId,
            items: _subjectItems,
            subTotal: roundNumber(_subTotal),
          },
        ],
        subTotal: roundNumber(_subTotal),
      };
    });

    const body = {
      code: "A",
      tendering: _id,
      subjects: [
        {
          _id: subjectId,
          items: _items,
        },
      ],
      advancedPayment: _advancedPayment,
      paymentTerms: _paymentTerms,
      netTotal: expectedGrandTotal,
      vat: roundNumber(expectedGrandTotal * 0.07),
      wht: wht ? roundNumber(expectedGrandTotal * 1.07 * 0.03 * -1) : undefined,
      grandTotal: roundNumber(
        wht ? expectedGrandTotal * 1.07 * 0.97 : expectedGrandTotal * 1.07
      ),
    };

    return body;
  };

  const _getCalculatedTotal = (number) => {
    let _total = number * 1.07;

    if (!details.payment.wht) {
      return _total;
    }

    return _total * 0.97;
  };

  const _uploadFile = async (key, file) => {
    const signedUrl = await getSignedUrl(key, file.type);

    const uploadResponse = await fetch(signedUrl, {
      method: "PUT",
      headers: {
        "Content-Type": file.type,
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: 0,
      },
      body: file,
    });

    if (uploadResponse.status !== 200) {
      return false;
    }

    return true;
  };

  const roundNumber = (num) => {
    return Number(Number(num).toFixed(2));
  };

  const handleSend = async () => {
    // * Upload logo file
    if (!logoFile) {
      toast.error("Logo file not found");
      return;
    }

    const { _id } = details;

    setSubmitting(true);

    const logoId = new ObjectId();
    const logoKey = `tenderings/${_id}/quotations/logos/${logoId}`;

    if (!(await _uploadFile(logoKey, logoFile))) {
      toast.error("Unable to upload logo file");
      setSubmitting(false);
      return;
    }

    let _body = {
      ...sanitizedData,
      logo: {
        _id: logoId,
        key: logoKey,
        name: logoFile.name,
        mimeType: logoFile.type,
        size: logoFile.size,
      },
      active: false,
    };

    try {
      const _createResult = await createQuotation(_body);

      const pdfId = new ObjectId();
      const pdfKey = `quotations/${_createResult._id}/files/${pdfId}`;

      // * Get PDF to save
      const _data = {
        ..._createResult,
        logo: `${assetsUri}/${logoKey}`,
        date: format(new Date(), "dd MMM yyyy"),
        appraiser: `${user?.firstname} ${user?.surname}`,
        owner: {
          name: owner.name,
          address: owner.address,
        },
        projectName: tenderingDetail?.name,
      };

      const pdf = await getPDF("quotation-design-report", _data);

      const pdfFile = new File(
        [new Blob([pdf.data])],
        `${_createResult.code}.pdf`
      );

      if (!(await _uploadFile(pdfKey, pdfFile))) {
        toast.error("Unable generate PDF file");
        setSubmitting(false);
        return;
      }

      await patchQuotation(_createResult._id, {
        active: true,
        file: {
          _id: pdfId,
          key: pdfKey,
          name: pdfFile.name,
          mimeType: pdfFile.type,
          size: pdfFile.size,
        },
      });

      toast.success("Send quotation successfully!");

      navigate(`/tenderings/${_id}`);
    } catch (error) {
      console.error(error);
      toast.error("Failed to send quotation");
    } finally {
      setSubmitting(false);
    }
  };

  const _getHeaderKeyValue = (title, value) => {
    return (
      <div className="tendering-quotation__design__summary__preview__header__text">
        <span className="tendering-quotation__design__summary__preview__header__text__title">
          {title}
        </span>
        <span className="tendering-quotation__design__summary__preview__header__text__value">
          {value}
        </span>
      </div>
    );
  };

  const downloadPDF = async () => {
    await toast.promise(
      new Promise(async (resolve, reject) => {
        const _data = {
          ...sanitizedData,
          logo: logoPreview,
          date: format(new Date(), "dd MMM yyyy"),
          appraiser: `${user?.firstname} ${user?.surname}`,
          owner: {
            name: owner.name,
            address: owner.address,
          },
          projectName: tenderingDetail?.name,
        };

        getPDF("quotation-design-report", _data)
          .then((response) => {
            const blob = new Blob([response.data]);

            // Create a temporary download link
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);

            let fileName = `(Draft) ${sanitizedData.code}.pdf`;

            link.download = fileName;
            document.body.appendChild(link);
            link.click();

            // Cleanup
            link.remove();
            window.URL.revokeObjectURL(link.href);

            resolve();
          })
          .catch((err) => {
            console.error(err);
            reject(err);
          });
      }),
      {
        pending: "Downloading file",
        success: "Quotation downloaded",
        error: "Failed to download",
      }
    );
  };

  if (!sanitizedData) return <></>;

  return (
    <Layout>
      <Layout.Header>
        <div className="tendering-quotation__design__summary__header">
          <h3>Summary</h3>
          <h4>Logo</h4>
          <div className="tendering-quotation__design__summary__header__logo">
            <DocumentUploader
              name="logo"
              accept="image/*"
              multiple={false}
              onChange={(files) => {
                if (!files || files.length === 0) {
                  setLogoFile(undefined);
                  setLogoPreview(undefined);
                  return;
                }
                setLogoFile(files.at(-1));
                const fr = new FileReader();
                fr.onload = function () {
                  setLogoPreview(fr.result);
                };
                fr.readAsDataURL(files.at(-1));
              }}
            />
            {logoPreview && <img src={logoPreview} height={100} />}
          </div>
        </div>
        <div
          style={{
            alignSelf: "flex-start",
            height: "41px",
            display: "flex",
            alignItems: "flex-end",
          }}
        >
          <Button
            label="Export Draft"
            variant="secondary"
            size="small"
            onClick={() => {
              downloadPDF();
            }}
          />
        </div>
      </Layout.Header>
      <Layout.Content>
        <div className="tendering-quotation__design__summary__preview">
          <div className="tendering-quotation__design__summary__preview__header">
            <div className="tendering-quotation__design__summary__preview__header__left">
              {logoPreview && (
                <img
                  className="tendering-quotation__design__summary__preview__header__logo"
                  src={logoPreview}
                />
              )}
              {_getHeaderKeyValue("Quotation No:", sanitizedData.code)}
              {_getHeaderKeyValue(
                "Quotation Date:",
                format(new Date(), "dd MMM yyyy")
              )}
              {_getHeaderKeyValue(
                "Appraisers",
                `${user?.firstname} ${user?.surname}`
              )}
            </div>
            <div className="tendering-quotation__design__summary__preview__header__right">
              {owner && (
                <>
                  {_getHeaderKeyValue("Project owner:", owner.name)}
                  {_getHeaderKeyValue("Address:", owner.address)}
                </>
              )}
              {_getHeaderKeyValue("Project name:", tenderingDetail?.name)}
            </div>
          </div>
          <table
            className="tendering-quotation__design__summary__preview__table"
            style={{ marginBottom: 36 }}
          >
            <thead>
              <tr>
                <th colSpan={"100%"}>Preliminary Price Summary</th>
              </tr>
              <tr>
                <th colSpan={2}>Description</th>
                <th style={{ width: 100 }}>Area</th>
                <th style={{ width: 160 }}>Price</th>
                <th style={{ width: 160 }}>Sub Total</th>
              </tr>
            </thead>
            <tbody>
              {sanitizedData?.subjects[0].items.map((item, itemIdx) => {
                return (
                  <>
                    <tr>
                      <td style={{ width: 0 }}>{itemIdx + 1}</td>
                      <td>{item.name}</td>
                      <td style={{ fontWeight: 500, textAlign: "center" }}>
                        {item.size.value}
                      </td>
                      <td style={{ fontWeight: 500, textAlign: "right" }}>
                        {formatNumber(item.price)}
                      </td>
                      <td style={{ fontWeight: 500, textAlign: "right" }}>
                        {formatNumber(item.size.value * item.price)}
                      </td>
                    </tr>
                    {item.remark && (
                      <tr>
                        <td style={{ borderRight: "none" }} />
                        <td style={{ borderLeft: "none" }}>{item.remark}</td>
                        <td />
                        <td />
                        <td />
                      </tr>
                    )}
                  </>
                );
              })}
              <tr>
                <td colSpan={2} style={{ textAlign: "right" }}>
                  Net Total
                </td>
                <td colSpan={3} style={{ textAlign: "right", fontWeight: 500 }}>
                  {formatNumber(sanitizedData.netTotal)}
                </td>
              </tr>
              <tr>
                <td colSpan={2} style={{ textAlign: "right" }}>
                  VAT (7%)
                </td>
                <td colSpan={3} style={{ textAlign: "right", fontWeight: 500 }}>
                  {formatNumber(sanitizedData.vat)}
                </td>
              </tr>
              {sanitizedData.wht && (
                <tr>
                  <td colSpan={2} style={{ textAlign: "right" }}>
                    WHT (-3%)
                  </td>
                  <td
                    colSpan={3}
                    style={{ textAlign: "right", fontWeight: 500 }}
                  >
                    {formatNumber(sanitizedData.wht)}
                  </td>
                </tr>
              )}
              <tr>
                <td colSpan={2} style={{ textAlign: "right" }}>
                  Grand Total
                </td>
                <td colSpan={3} style={{ textAlign: "right", fontWeight: 500 }}>
                  {formatNumber(sanitizedData.grandTotal)}
                </td>
              </tr>
            </tbody>
          </table>
          <table className="tendering-quotation__design__summary__preview__table">
            <thead>
              <tr>
                <th colSpan={"100%"}>Payment Terms</th>
              </tr>
              <tr>
                <th colSpan={2}>Description</th>
                <th style={{ width: 200 }}>Days for Submission</th>
                <th style={{ width: 200 }}>Cost</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ background: "rgb(238, 247, 255)" }}>
                <td style={{ width: 0 }}>1</td>
                <td>Advanced Payment</td>
                <td style={{ textAlign: "center" }}>
                  {sanitizedData.advancedPayment.days} days
                </td>
                <td style={{ textAlign: "right", fontWeight: 500 }}>
                  {formatNumber(
                    _getCalculatedTotal(sanitizedData.advancedPayment.total)
                  )}
                </td>
              </tr>
              {sanitizedData?.paymentTerms?.map((pt, ptIdx) => {
                const subject = pt.subjects[0];

                const _subjectMatched = sanitizedData.subjects.find(
                  (s) => s._id.toString() === subject.subject.toString()
                );

                const _item = _subjectMatched.items.find(
                  (i) => i._id.toString() === subject.items[0].item.toString()
                );

                const _isFinal =
                  ptIdx + 1 === sanitizedData?.paymentTerms.length;

                return (
                  <>
                    <tr style={{ background: "rgb(238, 247, 255)" }}>
                      <td>{ptIdx + 2}</td>
                      <td colSpan={_isFinal ? 2 : 1}>
                        {_isFinal ? "Final" : numberToOrdinal(ptIdx + 1)}{" "}
                        Payment
                      </td>
                      {!_isFinal && (
                        <td style={{ textAlign: "center" }}>
                          {subject.items[0].days} days
                        </td>
                      )}
                      <td style={{ textAlign: "right", fontWeight: 500 }}>
                        {formatNumber(_getCalculatedTotal(pt.subTotal))}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ borderRight: "none" }} />
                      <td style={{ borderLeft: "none" }} colSpan={2}>
                        {_item.name}
                      </td>
                      <td />
                    </tr>
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </Layout.Content>
      <Layout.Footer>
        <Button label="Return" variant={"secondary"} onClick={goBack} />
        <Button
          label="Send Quotation"
          onClick={handleSend}
          disabled={submitting}
        />
      </Layout.Footer>
    </Layout>
  );
};

Summary.propTypes = {};

export default Summary;
